import React, { useState, useEffect } from 'react';
import Confirmation from '../components/Confirmation';
import { AppFlow, IConfirmationResponse, IExpiredConfirmationResponse, IGetConfirmationResponse } from '@lib/types';
import { sources } from '../api/sources';
import { useNavigate } from 'react-router-dom';
import Spinner from '../components/Spinner';
import { ErrorType } from '../shared/types';
import { useGlobalState } from '../shared/state';
import { UtagHelper } from '../utils/utagHelper';
import { ClubHelper } from '@ace-clublabs/payments-helper';
import { ClubCodes, PaymentType, TransactionStatus } from '@ace-clublabs/payment-types';
import { mapMembershipLevelForTagging } from '@lib/shared';
import { Pages } from '../shared/pages';
import { autoPayPath } from '../shared/constants';
import StatusCode from 'status-code-enum';

const ConfirmationPage: React.FC = () => {
  const [isLoading, setLoading] = useState(true);
  const [, setClubCode] = useGlobalState('clubCode');
  const [membershipLevel] = useGlobalState('membershipLevel');
  const [campaignCode] = useGlobalState('campaignCode');
  const [amountDue] = useGlobalState('amountDue');
  const [appFlow] = useGlobalState('appFlow');
  const [minimalPage, setMinimalPage] = useState(false);
  const [confirmationDetails, setConfirmationDetails] = useState<IGetConfirmationResponse>(
    {} as IGetConfirmationResponse
  );
  const [autoPay] = useGlobalState('autoPay');
  const [isMobileApp] = useGlobalState('isMobileApp');
  const { eligible, discount } = autoPay || {};
  let navigate = useNavigate();

  let getConfirmationDetails = async () => {
    setLoading(true);
    const urlParams = new URLSearchParams(window.location.search);
    const transactionId = urlParams.get('transactionId') || '';
    try {
      const { status, data: confirmation } = await sources.getConfirmation({ transactionId });
      const isExpired = status === StatusCode.SuccessPartialContent;
      if (isExpired) {
        setMinimalPage(true);
      }
      tagConfirmation(confirmation, isExpired);
      setConfirmationDetails(confirmation);
      setLoading(false);
    } catch (e: any) {
      setClubCode(e?.clubCode);
      const isPaymentError = e?.type === ErrorType.PAYMENT_ERROR;
      if (isPaymentError) {
        navigate(Pages.Error, { state: { errorType: ErrorType.PAYMENT_ERROR } });
      } else {
        navigate(Pages.Error, { state: { errorType: ErrorType.SYSTEM_ERROR } });
      }
    }
  };

  useEffect(() => {
    getConfirmationDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const tagConfirmation = (confirmationDetails: IGetConfirmationResponse, isExpired: boolean) => {
    const missingDetails = !Object.keys(confirmationDetails).length;
    if (missingDetails) return;

    const { clubCode } = confirmationDetails as IExpiredConfirmationResponse;
    let club = ClubHelper.getClubAcronym(clubCode as ClubCodes);

    if (isExpired) {
      const expiredPageAppFlow = window?.location?.pathname.startsWith(autoPayPath) ? AppFlow.AUTOPAY : AppFlow.PAYMENT;
      const expiredTags = {
        ...UtagHelper.CommonTaggingValues(expiredPageAppFlow),
        page: UtagHelper.Pages(expiredPageAppFlow).expiredConfirmation,
        club,
      };
      return UtagHelper.tag(expiredTags);
    }

    const { autoPay, confirmationNumber, amountPaid, paymentType } = confirmationDetails as IConfirmationResponse;
    const mem_autopaycampaign = eligible && discount ? 'autopay required' : 'autopay not required';
    const isEnrolledInAutoPay = autoPay?.status === TransactionStatus.Succeeded;
    const autopay = isEnrolledInAutoPay ? 'olm enroll' : 'false';
    club = ClubHelper.getClubAcronym(clubCode as ClubCodes);

    const transaction_type =
      paymentType === PaymentType.CreditOrDebit
        ? 'creditcard'
        : paymentType === PaymentType.DebitOnly
        ? 'debit'
        : paymentType === PaymentType.Google
        ? 'google pay'
        : paymentType === PaymentType.Apple
        ? 'apple pay'
        : 'echeck';

    const mem_level = mapMembershipLevelForTagging(membershipLevel);
    const total = discount ? amountPaid + discount : amountPaid;
    const transactions = [total];
    if (isEnrolledInAutoPay) {
      transactions.push(-(discount || 0));
    }

    const transaction_amount = transactions.map((transaction) => transaction.toFixed(2));

    let flowSpecificTags: any;
    let category: string[] = [];
    let product: string[] = [];
    let quantity: string[] = [];
    let transactionAmount: string[] = [];

    if (appFlow === AppFlow.AUTOPAY) {
      if (amountPaid && isEnrolledInAutoPay) {
        category = ['membership:easy auto pay', 'membership:easy auto pay'];
        product = ['regular', 'AUTOPAY'];
        quantity = ['1', '1'];
        transactionAmount = transaction_amount;
      } else if (amountPaid && !isEnrolledInAutoPay) {
        category = ['membership:easy auto pay'];
        product = ['regular'];
        quantity = ['1'];
        transactionAmount = [amountPaid.toFixed(2)];
      } else {
        category = ['membership:easy auto pay'];
        product = ['AUTOPAY'];
        quantity = ['1'];
        transactionAmount = ['0.00'];
      }
      flowSpecificTags = {
        category,
        product,
        quantity,
        transaction_amount: transactionAmount,
      };
    } else {
      flowSpecificTags = {
        category: isEnrolledInAutoPay ? ['membership:easy pay', 'membership:easy pay'] : ['membership:easy pay'],
        product: isEnrolledInAutoPay ? ['regular', 'AUTOPAY'] : ['regular'],
        quantity: isEnrolledInAutoPay ? ['1', '1'] : ['1'],
        transaction_amount,
      };
    }

    const defaultTags = {
      ...UtagHelper.CommonTaggingValues(appFlow),
      ...flowSpecificTags,
      club,
      confirmation_id: confirmationNumber,
      mem_level,
      page: UtagHelper.Pages(appFlow).confirmation,
      transaction_type,
    };
    const tags = {
      ...defaultTags,
      autopay,
      mem_autopay: `${isEnrolledInAutoPay}`,
      mem_autopaycampaign,
      bill_status: amountDue > 0 ? 'due' : 'not due',
      campaigncodemr: campaignCode,
    };

    appFlow === AppFlow.AUTOPAY ? UtagHelper.tag(defaultTags) : UtagHelper.tag(tags);
  };

  return isLoading ? (
    <Spinner />
  ) : (
    <Confirmation {...confirmationDetails} isMobileApp={isMobileApp} minimalPage={minimalPage} />
  );
};

export default ConfirmationPage;
